"use client";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";

import ProductList from "@/components/ProductList";
import {
  fetchVoucherOffers,
  fetchVoucherFilterOptions,
} from "@/utils/api/voucher";
import VoucherFilters from "@/features/landing/components/VoucherFilters";
import VoucherFilterDrawer from "@/features/landing/components/VoucherFilterDrawer";
import { enrichOffer } from "@/features/landing/utils/enrichOffer";
import { countryName, toA2 } from "@/features/landing/utils/country-name";

// Stable default so callers that omit `brands` don't pass a fresh {} every
// render — a new identity here makes `fetchPage` (memoized on `brands`) change
// every render, which re-triggers the fetch effect in an infinite loop and
// makes the "Updating…" banner flicker (visible as cards shaking) once a filter
// is active.
const EMPTY_BRANDS = {};

export default function VoucherSection({ selectedProduct, onSelect, initial, brands = EMPTY_BRANDS }) {
  // ---------------------------
  // State
  // ---------------------------
  const [vouchers, setVouchers] = useState(initial?.list ?? []);
  const [totalCount, setTotalCount] = useState(initial?.total ?? 0);
  const [page, setPage] = useState(Math.floor((initial?.offset ?? 0) / 20) + 1);

  const [isLoading, setIsLoading] = useState(false);   // initial load
  const [isUpdating, setIsUpdating] = useState(false); // subsequent updates

  const [voucherFilters, setVoucherFilters] = useState({
    brand: "",
    country: "",
    regions: "",
    subType: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(voucherFilters);

  const [showFilters, setShowFilters] = useState(true);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    subTypes: [],
    countries: [],
    regions: [],
  });

  const perPage = 20;

  // ---------------------------
  // Refs
  // ---------------------------
  const controllerRef = useRef(null);
  const fetchIdRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  // ---------------------------
  // Helpers
  // ---------------------------
const getCountryName = useCallback((code) => countryName(code), []);

  const noFiltersSelected = useMemo(() => {
    // use source-of-truth filters here (not debounced)
    return (
      !voucherFilters.brand &&
      !voucherFilters.country &&
      !voucherFilters.regions &&
      !voucherFilters.subType
    );
  }, [voucherFilters]);

  // ---------------------------
  // One-time / lifecycle effects
  // ---------------------------

  // Mark "loaded once" if SSR/initial provided list
  useEffect(() => {
    if (initial?.list?.length) hasLoadedOnceRef.current = true;
  }, [initial]);

  // Abort on unmount
  useEffect(() => () => controllerRef.current?.abort(), []);

  // ---------------------------
  // Core fetcher (defined BEFORE effects that use it)
  // ---------------------------
  const fetchPage = useCallback(
    async ({ pageArg, filtersArg }) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const fetchId = ++fetchIdRef.current;

      const offset = (pageArg - 1) * perPage;

      const clean = { ...filtersArg };
      Object.keys(clean).forEach((k) => {
        if (!clean[k]) delete clean[k];
      });
        if (clean.country) {
          if (String(clean.country).toUpperCase() !== "GLOBAL") {
            clean.country = toA2(clean.country);
          } else {
            clean.country = "GLOBAL";
          }
        }
      const isFirstLoad = !hasLoadedOnceRef.current;
      isFirstLoad ? setIsLoading(true) : setIsUpdating(true);

      try {
        const { list, total } = await fetchVoucherOffers({
          ...clean,
          limit: perPage,
          offset,
          signal: controller.signal,
        });

        if (controller.signal.aborted || fetchIdRef.current !== fetchId) return;

       const enriched = Object.keys(brands).length
          ? list.map((t) => ({ ...enrichOffer(t, brands), type: "voucher" }))
          : list.map((t) => ({ ...t, type: "voucher" }));

        setVouchers(enriched);
        setTotalCount(total);
        hasLoadedOnceRef.current = true;
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("❌ Failed to fetch vouchers:", error);
        }
      } finally {
        if (fetchIdRef.current === fetchId) {
          setIsLoading(false);
          setIsUpdating(false);
        }
      }
    },
    [brands, perPage]
  );

  // ---------------------------
  // Debounce filters + reset page
  // ---------------------------
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters(voucherFilters);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [voucherFilters]);

  // ---------------------------
  // Fetch offers when page or debounced filters change
  // (Skip initial fetch if SSR data exists, page=1, and no filters)
  // ---------------------------
  useEffect(() => {
    if (page === 1 && noFiltersSelected && initial?.list?.length) return;
    fetchPage({ pageArg: page, filtersArg: debouncedFilters });
  }, [page, debouncedFilters, fetchPage, initial?.list?.length, noFiltersSelected]);

  // ---------------------------
 
  // ---------------------------
// Fetch filter options (debounced to avoid spamming API)
const filterOptionsFetchIdRef = useRef(0);

useEffect(() => {
  let alive = true;
  const fetchId = ++filterOptionsFetchIdRef.current;

  (async () => {
    try {
      const options = await fetchVoucherFilterOptions(debouncedFilters);

      if (!alive || filterOptionsFetchIdRef.current !== fetchId) return;

      const normalizedCountries = (options?.countries || [])
        .map((c) => toA2(c))
        .filter(Boolean);

      const uniqCountries = Array.from(new Set(normalizedCountries));

      uniqCountries.sort((a, b) =>
        countryName(a).localeCompare(countryName(b))
      );

      setFilterOptions({
        ...options,
        countries: uniqCountries,
      });
    } catch (err) {
      if (!alive || filterOptionsFetchIdRef.current !== fetchId) return;
      console.error("❌ Failed to fetch dynamic filter options:", err);
    }
  })();

  return () => {
    alive = false;
  };
}, [debouncedFilters]);

  // ---------------------------
 // Re-enrich when brands map arrives from parent (no refetch)
  // ---------------------------
  useEffect(() => {
    if (!vouchers.length || !Object.keys(brands).length) return;
    setVouchers((prev) =>
      prev.map((t) => ({ ...enrichOffer(t, brands), type: "voucher" }))
    );
  }, [brands, vouchers.length]);

  // ---------------------------
  // Derived
  // ---------------------------
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / perPage)),
    [totalCount, perPage]
  );

  // ---------------------------
  // Render
  // ---------------------------

  return (
   <div className="mt-4 grid gap-4 lg:gap-6 lg:grid-cols-[280px_1fr] items-start relative">


      {/* 🔍 Floating Filter Button (Mobile Only) */}
      {!filterDrawerOpen && (
      <button
  onClick={() => setFilterDrawerOpen(true)}
  className="fixed bottom-4 right-4 bg-primary text-white px-4 py-3 rounded-full shadow-md z-50 sm:hidden inline-flex items-center gap-2 text-sm font-medium"
  aria-label="Open voucher filters"
>
  🔍 Filters
</button>

      )}

      {/* 📱 Mobile Drawer Filter */}
      <VoucherFilterDrawer
        isOpen={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={voucherFilters}
        setFilters={setVoucherFilters}
        filterOptions={filterOptions}
        onReset={() => {
          setVoucherFilters({ brand: "", country: "", regions: "", subType: "" });
          setPage(1);
        }}
        getCountryName={getCountryName}
      />


     {/* 💻 Desktop Filters (sticky left rail) */}
<aside className="hidden lg:block sticky top-16 max-h-[calc(100vh-4rem)] overflow-auto">
  <div className="bg-surface border border-border rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-textSecondary">Voucher Filters</h3>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowFilters((prev) => !prev)}
          className="text-xs underline text-textSecondary hover:text-textPrimary"
        >
          {showFilters ? "Hide" : "Show"}
        </button>
        <button
          onClick={() => {
            setVoucherFilters({ brand: "", country: "", regions: "", subType: "" });
            setPage(1);
          }}
          className="text-xs underline text-textSecondary hover:text-textPrimary"
        >
          Reset
        </button>
      </div>
    </div>
    {showFilters && (
      <VoucherFilters
        filters={voucherFilters}
        setFilters={setVoucherFilters}
        filterOptions={filterOptions}
        show={true}
        getCountryName={getCountryName}
      />
    )}
  </div>
</aside>



         {/* 🧾 List + Loading (right content) */}
     <section>
       {vouchers.length === 0 ? (
         isLoading ? (
           <div className="text-center text-sm text-textSecondary mt-6">Loading vouchers…</div>
         ) : (
           <div className="text-center text-textSecondary text-sm my-8">
             No vouchers found. Try adjusting your filters.
           </div>
         )
       ) : (
         <>
           {isUpdating && (
             <div className="text-center text-xs text-textSecondary mb-2">Updating…</div>
           )}
         <ProductList
  products={vouchers}
  productType="VOUCHER"
  selectedProduct={selectedProduct}
  onSelect={onSelect}
  page={page}
  totalPages={totalPages}
  onPageChange={setPage}
  variant="compact"
  columns="grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
  gap="gap-3 md:gap-4"
/>

         </>
       )}
     </section>
    </div>
  );
}
